/**
 * The one rule for a 401 (B8), for both HTTP clients: the session expired.
 *
 * The cached user is dropped (an auth endpoint's own 401 is a failed sign-in,
 * not an expiry). Where a page may leave for sign-in, it goes to the sign-in
 * page with a `returnTo` that brings the user back to the same page; the
 * builder's unsaved edits are kept in the tab meanwhile (B4).
 */

/**
 * Pages a 401 never takes to sign-in: the auth pages themselves, and public
 * or share surfaces where guests legitimately have no session.
 */
export const NO_SIGN_IN_REDIRECT_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/share',
  '/public',
  '/internal',
  '/auth',
  '/audiences/invites',
]

/** The sign-in page, returning to where the user is now. */
export function signInUrl(): string {
  const back = `${window.location.pathname}${window.location.search}`
  return `/login?returnTo=${encodeURIComponent(back)}`
}

/**
 * Apply the 401 rule to a failed request. `redirect` says whether this
 * client's callers leave for sign-in on their own page (the app's API client)
 * or report the expiry themselves (the auth provider and the builder, through
 * the typed `session_expired` transport kind and its Sign in action).
 */
export function handleUnauthorized(
  error: { response?: { status?: number }; config?: { url?: string } },
  options: { redirect: boolean },
): void {
  if (error.response?.status !== 401) return
  const isAuthEndpoint = error.config?.url?.includes('/auth/') ?? false
  if (isAuthEndpoint) return
  try {
    localStorage.removeItem('user')
  } catch {
    // Storage unavailable: nothing cached to drop.
  }
  if (!options.redirect || typeof window === 'undefined') return
  const onNoRedirectPath = NO_SIGN_IN_REDIRECT_PATHS.some((path) =>
    window.location.pathname.startsWith(path),
  )
  if (!onNoRedirectPath) window.location.href = signInUrl()
}
